import type { RouteRecordRaw } from "vue-router"

import { AcimaApplyRoutes } from "@/providers/acima_standard"
import { AdsApplyRoutes } from "@/providers/ads_standard"
import { AffApplyRoutes } from "@/providers/aff_standard"
import { SynchronyApplyRoutes } from "@/providers/synchrony_standard"
import { FlexShopperApplyRoutes } from "@/providers/flexshopper_standard"
import { GenesisApplyRoutes } from "@/providers/genesis_standard"
import { ProgressiveApplyRoutes } from "@/providers/progressive_standard"
import { FortivaApplyRoutes } from "@/providers/fortiva_standard"
import { KoalafiApplyRoutes } from "@/providers/koalafi_standard"
import { KafeneApplyRoutes } from "@/providers/kafene_standard"

import { FormRoutes } from "./application"
import { SnapApplyRoutes } from "@/providers/snap_standard"

import { GroupRoutes } from "./groups/routes"
import { WorkflowRoutes } from "./workflows/routes"
import { GenericDecisionRoutes } from "./decision/routes"
import { TdBankApplyRoutes } from "@/providers/tdbank_standard"
import {
  useProveConfig,
  useProveStore,
  useRequireSelectedPlan,
} from "@versatile-credit-apply/sdk"
import type { ProviderKey } from "@versatile-credit-apply/omni-apply-client"

export const ApplyRoutes: RouteRecordRaw[] = [
  {
    name: "apply-routes",
    path: "apply",
    children: [
      {
        // Start to check if we have any pre-provider workflows
        name: "apply-workflow-route",
        path: "",
        redirect: ({ fullPath }) => `${fullPath}/workflows/pre-provider`,
      },
      {
        name: "apply-group-routes",
        path: "groups",
        children: GroupRoutes,
      },
      {
        name: "apply-provider-routes",
        path: "providers",
        children: [
          {
            name: "application-default",
            path: ":providerKey/application",
            component: () => import("./ApplicationFormPage.vue"),
            meta: {
              step: (steps) =>
                steps.includes("reviewOffer") ? "reviewOffer" : "applicant",
            },
            children: FormRoutes,
            beforeEnter: () => {
              const proveConfig = useProveConfig()
              const proveStore = useProveStore()

              if (
                proveConfig.value?.allowManualEntry === false &&
                !proveStore.completed
              ) {
                return {
                  name: "prove-challenge",
                }
              }
            },
          },
          {
            name: "submit-application-default",
            path: ":providerKey/submit",
            component: () => import("./SubmitApplicationPage.vue"),
            meta: {
              step: {
                prequalify: "applicant",
                "*": "terms",
              },
            },
            props: {
              acceptOffer: false,
            },
          },
          {
            name: "explore-offers",
            path: ":providerKey/decision/explore-offers",
            component: () => import("./plan/ExploreOffersPage.vue"),
            meta: {
              preventBack: true,
              step: "decision",
              next: "select-offer",
            },
          },
          {
            name: "select-offer",
            path: ":providerKey/offer/:planCode",
            // Only Synchrony is supported at this time
            component: () =>
              import("@/providers/synchrony_standard/pages/SynchronySelectOfferPage.vue"),
            meta: {
              preventBack: true,
              step: "decision",
              next(route) {
                const providerKey = route.params.providerKey as ProviderKey
                const planCode = route.params.planCode as string
                const selectedPlan = useRequireSelectedPlan(
                  () => providerKey,
                  () => planCode
                )
                return selectedPlan.value.providerProductType === "loan"
                  ? "review-offer"
                  : "../terms"
              },
            },
          },
          {
            name: "accept-offer-default",
            path: ":providerKey/accept-offer",
            component: () => import("./SubmitApplicationPage.vue"),
            meta: {
              step: "terms",
            },
            props: {
              acceptOffer: true,
            },
          },
          {
            name: "switch-provider-start",
            path: ":providerKey/:mode/switch",
            component: () => import("./SwitchProviderPage.vue"),
            props: true,
          },
          {
            name: "patch-default",
            path: ":providerKey/patch",
            component: () => import("./PatchApplicationPage.vue"),
            meta: {
              next: "patch-success",
            },
          },
          ...AcimaApplyRoutes,
          ...AdsApplyRoutes,
          ...AffApplyRoutes,
          ...FlexShopperApplyRoutes,
          ...FortivaApplyRoutes,
          ...GenesisApplyRoutes,
          ...KoalafiApplyRoutes,
          ...KafeneApplyRoutes,
          ...ProgressiveApplyRoutes,
          ...SnapApplyRoutes,
          ...SynchronyApplyRoutes,
          ...TdBankApplyRoutes,
        ],
      },
      ...GenericDecisionRoutes,
      ...WorkflowRoutes,
    ],
  },
]
